import { IBusinessAttribute, SelectorTypes } from '@site-mate/sitemate-global-shared';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { FieldValues, UseFormRegister, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { workspaceService } from '@/services/global-api/workspace.service';
import { SignUpSteps } from '@/stores/signup.store';

export type SignUpStepProps<T extends FieldValues> = {
  loading?: boolean;
  form: UseFormReturn<T>;
  signUpStep?: SignUpSteps;
  hasEmailError?: boolean;
  register: UseFormRegister<T>;
  onNextStep?: () => void;
  setReCAPTCHAV2Token?: (token: string | null) => void;
};

export type WorkspaceBrandingForm = {
  logoUrl: string | null;
  color: string;
  newLogo?: File | string | null;
};

interface IPasswordValidation {
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasMinLength: boolean;
}

export const businessAttributeOtherOption = {
  name: 'Other',
  code: 'Other',
};

export const businessAttributesPlaceholders = {
  [SelectorTypes.Vertical]: 'Industry',
  [SelectorTypes.Role]: 'Role',
  [SelectorTypes.Discipline]: 'Role',
};

export const validatePassword = (password: string): IPasswordValidation => {
  const minLength = 10;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasMinLength = password.length >= minLength;

  return {
    hasLowercase,
    hasUppercase,
    hasNumber,
    hasMinLength,
  };
};

const createEmailRegex = (): RegExp => {
  const DomainRegex = /^(?:\[[0-9]{1,3}(?:\.[0-9]{1,3}){3}\]|(?:[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*\.)+[a-zA-Z]{2,})$/;
  const localPart = '(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))';
  const ipDomain = '(\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])';
  const standardDomain = DomainRegex.source.substring(1, DomainRegex.source.length - 1);

  const emailRegex = new RegExp(`^${localPart}@(${ipDomain}|${standardDomain})$`);
  return emailRegex;
};

export const createCombinedPasswordValidationRegex = (): RegExp => {
  return /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{10,}$/;
};

export const createDoNotAcceptWithoutAlphanumericRegex = (): RegExp => {
  return new RegExp('^.*[a-zA-Z0-9].*$');
};

export const phoneUtil = PhoneNumberUtil.getInstance();
export const hasPhoneValue = (phone: string) => {
  try {
    const parsedPhone = phoneUtil.parseAndKeepRawInput(phone);
    return !!parsedPhone.getNationalNumber();
  } catch {
    return false;
  }
};
export const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const emailSchema = z.string().regex(createEmailRegex(), {
  message: 'Please enter a valid email address in the format name@example.com.',
});

export const validateUniqueUserEmailSchema = z.object({
  status: z.number(),
  product: z.enum(['GB', 'SM']).optional(),
});

export type ValidateUniqueUserEmailData = z.infer<typeof validateUniqueUserEmailSchema>;

export const signUpFormSchema = z
  .object({
    email: emailSchema,
    firstName: z.string().regex(createDoNotAcceptWithoutAlphanumericRegex(), {
      message: 'Required',
    }),
    lastName: z.string().regex(createDoNotAcceptWithoutAlphanumericRegex(), {
      message: 'Required',
    }),
    workspaceName: z
      .string()
      .regex(createDoNotAcceptWithoutAlphanumericRegex(), {
        message: 'Workspace name should have one or more alphanumeric characters.',
      })
      .refine(
        async (workspaceName) => {
          if (!workspaceName) return false;

          return await workspaceService
            .checkForBannedOrReservedWords(workspaceName.toLowerCase())
            .then(() => true)
            .catch(() => false);
        },
        {
          message: 'Workspace names cannot contain inappropriate or reserved terms. Please use a different name.',
        },
      ),
    password: z.string().regex(createCombinedPasswordValidationRegex()),
    businessAttributes: z.object({
      vertical: z.custom<IBusinessAttribute>().nullable(),
      role: z.custom<IBusinessAttribute>().nullable(),
      discipline: z.custom<IBusinessAttribute>().nullable(),
    }),
    mobile: z
      .string()
      .refine((phone) => hasPhoneValue(phone), {
        message: 'Required',
      })
      .refine((phone) => isPhoneValid(phone), {
        message: 'Invalid mobile number - incorrect length',
      }),
    timezone: z.string(),
    continent: z.string(),
    isSSOSignUp: z.boolean(),
  })
  .superRefine((form, ctx) => {
    if (!form.isSSOSignUp && !form.password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Password is required',
      });
    }
  });
export type SignUpFormData = z.infer<typeof signUpFormSchema>;
